import { Button, Input } from "@norican/react";
import { useMemo, useRef, useState } from "react";

import { control, subText } from "@/components/table";
import type { OrgPerson } from "@/lib/api";
import type { OrgMap } from "@/lib/org";
import { BRAND, buildSlide, SLIDE_W, type SlideBox, type SlideModel } from "@/lib/org-slide";

/**
 * "Presentation / PDF": the chart as a slide, exportable three ways.
 *
 * The page is always light and always in Norican brand colours, whatever the
 * app theme: it is made to be pasted into a deck or sent to someone. PNG and
 * PDF are pictures of the preview; the PowerPoint file is built from the same
 * geometry as native shapes, so every name and title can be edited there.
 */

const FONT = "Manrope, 'Segoe UI', sans-serif";
const DISPLAY = "Turbine, Manrope, 'Segoe UI', sans-serif";
/** Norican decks use Meiryo (norican-exec-deck); every Windows laptop has it. */
const PPT_FONT = "Meiryo";

function boxStyle(b: SlideBox): React.CSSProperties {
  const base: React.CSSProperties = {
    position: "absolute",
    left: b.x,
    top: b.y,
    width: b.w,
    height: b.h,
    borderRadius: 9,
    padding: "8px 11px 8px 12px",
    boxSizing: "border-box",
    overflow: "hidden",
  };
  const edge = b.moved ? { boxShadow: `inset 4px 0 0 ${BRAND.gold}` } : {};
  switch (b.kind) {
    case "context":
      return {
        ...base,
        border: `1.5px dashed ${BRAND.line}`,
        color: BRAND.muted,
        textAlign: "center",
        borderRadius: 10,
      };
    case "root":
      return {
        ...base,
        background: BRAND.navy,
        color: "#fff",
        textAlign: "center",
        borderRadius: 12,
        padding: "12px 20px",
        ...edge,
      };
    case "manager":
      return { ...base, background: BRAND.blue, color: "#fff", ...edge };
    case "label":
      return {
        ...base,
        background: BRAND.lightBlue,
        color: BRAND.blue,
        fontWeight: 700,
        fontSize: 15.5,
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };
    default:
      return {
        ...base,
        background: BRAND.card,
        border: `1px solid ${BRAND.cardLine}`,
        color: BRAND.navy,
        ...edge,
      };
  }
}

export function SlideView({
  model,
  innerRef,
}: {
  model: SlideModel;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={innerRef}
      style={{
        position: "relative",
        width: model.width,
        height: model.height,
        background: "#fff",
        fontFamily: FONT,
        color: BRAND.navy,
      }}
    >
      <svg
        width={model.width}
        height={model.height}
        style={{ position: "absolute", left: 0, top: 0 }}
      >
        {model.lines.map((l, i) => (
          <line
            key={i}
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            stroke={l.tone === "main" ? BRAND.blue : l.tone === "sub" ? BRAND.subLine : BRAND.line}
            strokeWidth={2}
          />
        ))}
      </svg>
      {model.texts.map((t, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: t.x,
            top: t.y,
            width: t.w,
            fontSize: t.size,
            textAlign: t.align,
            color: t.tone === "muted" ? BRAND.muted : BRAND.navy,
            fontFamily: t.font === "display" ? DISPLAY : FONT,
            lineHeight: 1.1,
          }}
        >
          {t.text}
        </div>
      ))}
      {model.boxes.map((b, i) => (
        <div key={i} style={boxStyle(b)}>
          {b.kind === "label" ? (
            b.name
          ) : b.name ? (
            <>
              <div
                style={{
                  fontWeight: b.kind === "root" ? 800 : 700,
                  fontSize: b.kind === "root" ? 21 : b.kind === "context" ? 15 : 15.5,
                  lineHeight: 1.2,
                  paddingRight: b.country && b.kind !== "root" ? 30 : 0,
                }}
              >
                {b.name}
              </div>
              {b.title ? (
                <div
                  style={{
                    fontSize: b.kind === "root" ? 15 : 12.5,
                    marginTop: 2,
                    lineHeight: 1.25,
                    opacity: b.kind === "member" || b.kind === "context" ? 1 : 0.88,
                    color: b.kind === "member" ? "#3E4A60" : undefined,
                    paddingRight: b.country && b.kind !== "root" ? 26 : 0,
                  }}
                >
                  {b.title}
                </div>
              ) : null}
              {b.country && b.kind !== "root" ? (
                <span
                  style={{
                    position: "absolute",
                    right: 9,
                    top: 9,
                    fontSize: 11,
                    fontWeight: 700,
                    borderRadius: 5,
                    padding: "1px 5px",
                    background: b.kind === "manager" ? "rgba(255,255,255,.18)" : BRAND.lightBlue,
                    color: b.kind === "manager" ? "#fff" : BRAND.blue,
                  }}
                >
                  {b.country}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ─── exports ───────────────────────────────────────────────────────────────

function slug(s: string): string {
  return (
    s
      .replace(/[^\w-]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "") || "org-chart"
  );
}

async function toPng(node: HTMLElement): Promise<string> {
  const { toPng: render } = await import("html-to-image");
  return render(node, { pixelRatio: 2, backgroundColor: "#ffffff", cacheBust: true });
}

function download(href: string, name: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.click();
}

async function exportPdf(node: HTMLElement, model: SlideModel, name: string) {
  // JPEG, not PNG: jsPDF stores a PNG nearly uncompressed (23 MB for the IT
  // chart); a high-quality JPEG of the same 2x render is a few hundred kB.
  const { toJpeg } = await import("html-to-image");
  const img = await toJpeg(node, {
    pixelRatio: 2,
    backgroundColor: "#ffffff",
    quality: 0.92,
    cacheBust: true,
  });
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({
    orientation: model.width >= model.height ? "landscape" : "portrait",
    unit: "px",
    format: [model.width, model.height],
    hotfixes: ["px_scaling"],
  });
  pdf.addImage(img, "JPEG", 0, 0, model.width, model.height);
  pdf.save(`${name}.pdf`);
}

/** Native PowerPoint: shapes, text and lines, nothing rasterised. */
async function exportPptx(model: SlideModel, name: string) {
  const { default: PptxGenJS } = await import("pptxgenjs");
  const pptx = new PptxGenJS();
  const W = 13.333; // inches, 16:9 width
  const k = W / SLIDE_W;
  const H = Math.max(7.5, model.height * k);
  pptx.defineLayout({ name: "HROPS", width: W, height: H });
  pptx.layout = "HROPS";
  pptx.title = name;
  const slide = pptx.addSlide();
  const hex = (c: string) => c.replace("#", "");
  const pt = (px: number) => px * k * 72; // font px on the canvas → points on the slide

  for (const l of model.lines) {
    const x = Math.min(l.x1, l.x2) * k;
    const y = Math.min(l.y1, l.y2) * k;
    slide.addShape(pptx.ShapeType.line, {
      x,
      y,
      w: Math.max(Math.abs(l.x2 - l.x1) * k, 0.0001),
      h: Math.max(Math.abs(l.y2 - l.y1) * k, 0.0001),
      line: {
        color: hex(l.tone === "main" ? BRAND.blue : l.tone === "sub" ? BRAND.subLine : BRAND.line),
        width: 1.25,
      },
    });
  }
  for (const b of model.boxes) {
    const fill =
      b.kind === "root"
        ? BRAND.navy
        : b.kind === "manager"
          ? BRAND.blue
          : b.kind === "label"
            ? BRAND.lightBlue
            : b.kind === "context"
              ? "#FFFFFF"
              : BRAND.card;
    const ink =
      b.kind === "root" || b.kind === "manager"
        ? "FFFFFF"
        : b.kind === "label"
          ? hex(BRAND.blue)
          : hex(BRAND.navy);
    const box = { x: b.x * k, y: b.y * k, w: b.w * k, h: b.h * k };
    slide.addShape(pptx.ShapeType.roundRect, {
      ...box,
      rectRadius: 0.06,
      fill: { color: hex(fill) },
      line:
        b.kind === "context"
          ? { color: hex(BRAND.line), width: 1, dashType: "dash" }
          : b.kind === "member"
            ? { color: hex(BRAND.cardLine), width: 0.75 }
            : { type: "none" },
    });
    if (b.moved) {
      slide.addShape(pptx.ShapeType.rect, {
        x: box.x,
        y: box.y + 0.02,
        w: 0.045,
        h: box.h - 0.04,
        fill: { color: hex(BRAND.gold) },
        line: { type: "none" },
      });
    }
    if (!b.name) {
      continue;
    }
    const center = b.kind === "root" || b.kind === "context" || b.kind === "label";
    const runs: { text: string; options: Record<string, unknown> }[] = [
      {
        text: b.name,
        options: {
          bold: true,
          fontSize: pt(b.kind === "root" ? 21 : 15.5),
          color: b.kind === "context" ? hex(BRAND.muted) : ink,
          breakLine: !!b.title,
        },
      },
    ];
    if (b.title) {
      runs.push({
        text: b.title,
        options: {
          fontSize: pt(b.kind === "root" ? 15 : 12.5),
          color: b.kind === "member" ? "3E4A60" : b.kind === "context" ? hex(BRAND.muted) : ink,
        },
      });
    }
    slide.addText(runs, {
      ...box,
      w: box.w - (b.country && !center ? 0.3 : 0),
      fontFace: PPT_FONT,
      align: center ? "center" : "left",
      valign: "middle",
      margin: [2, 6, 2, 10],
      fit: "shrink",
    });
    if (b.country && !center) {
      slide.addText(b.country, {
        x: box.x + box.w - 0.3,
        y: box.y + 0.05,
        w: 0.26,
        h: 0.16,
        fontFace: PPT_FONT,
        fontSize: pt(10.5),
        bold: true,
        align: "center",
        color: b.kind === "manager" ? "FFFFFF" : hex(BRAND.blue),
        fill: { color: b.kind === "manager" ? "3F7DDE" : hex(BRAND.lightBlue) },
        margin: 0,
      });
    }
  }
  for (const t of model.texts) {
    slide.addText(t.text, {
      x: t.x * k,
      y: t.y * k,
      w: t.w * k,
      h: t.size * 1.5 * k,
      fontFace: PPT_FONT,
      fontSize: pt(t.size),
      color: hex(t.tone === "muted" ? BRAND.muted : BRAND.navy),
      align: t.align,
      margin: 0,
      valign: "top",
    });
  }
  await pptx.writeFile({ fileName: `${name}.pptx` });
}

// ─── dialog ────────────────────────────────────────────────────────────────

export function PresentationDialog({
  map,
  rootId,
  movedIds,
  scopeIds,
  defaultTitle,
  defaultSubtitle,
  source,
  onClose,
}: {
  map: OrgMap;
  rootId: string;
  /** People whose manager changes: the gold edge. */
  movedIds: Set<string>;
  /** When given, the people a scenario is about; the chart can be limited to them. */
  scopeIds?: Set<string>;
  defaultTitle: string;
  defaultSubtitle: string;
  source: string;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [onlyScope, setOnlyScope] = useState(!!scopeIds && scopeIds.size > 0);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const node = useRef<HTMLDivElement>(null);

  const model = useMemo(
    () =>
      buildSlide({
        map,
        rootId,
        include: (p: OrgPerson) =>
          p.person !== false && (!onlyScope || !scopeIds || scopeIds.has(p.id)),
        moved: (id) => movedIds.has(id),
        title,
        subtitle,
        source,
      }),
    [map, rootId, onlyScope, scopeIds, movedIds, title, subtitle, source],
  );
  const name = `${slug(title)}_${new Date().toISOString().slice(0, 10)}`;
  const run = (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    setError(null);
    fn()
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(null));
  };
  const scale = 0.5;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-bg border-border w-full max-w-[1040px] rounded-lg border p-4 shadow-xl">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-fg-muted flex min-w-72 flex-1 flex-col text-xs">
            Title
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="text-fg-muted flex min-w-72 flex-1 flex-col text-xs">
            Subtitle ({"{n}"} = head count)
            <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
          </label>
          {scopeIds && scopeIds.size > 0 ? (
            <label className="text-fg-muted flex items-center gap-2 pb-2 text-xs">
              <input
                type="checkbox"
                checked={onlyScope}
                onChange={(e) => setOnlyScope(e.target.checked)}
              />
              Only people in this scenario
            </label>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!!busy}
            onClick={() => run("pdf", () => exportPdf(node.current!, model, name))}
          >
            {busy === "pdf" ? "Making PDF…" : "Download PDF"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!!busy}
            onClick={() => run("pptx", () => exportPptx(model, name))}
          >
            {busy === "pptx" ? "Making PowerPoint…" : "Download PowerPoint"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!!busy}
            onClick={() =>
              run("png", async () => download(await toPng(node.current!), `${name}.png`))
            }
          >
            {busy === "png" ? "Making PNG…" : "Download PNG"}
          </Button>
          <span className={subText}>
            {model.people} people. The PowerPoint is built from shapes, so every box and name can be
            edited there.
          </span>
          <button className={`${control} ml-auto`} onClick={onClose}>
            Close
          </button>
        </div>
        {error ? <p className="text-danger-fg mt-2 text-sm">{error}</p> : null}
        <div
          className="border-border mt-3 overflow-hidden rounded border"
          style={{ width: SLIDE_W * scale, height: model.height * scale }}
        >
          <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
            <SlideView model={model} innerRef={node} />
          </div>
        </div>
      </div>
    </div>
  );
}
